import { MAX_FIGHTERS, MAX_HUMANS } from '@/components'
import { defineResource } from '@/core/ecs/World'
import {
  P1_KEYS,
  P2_KEYS,
  PAD_BINDING,
  STICK_DEADZONE,
  TRIGGER_THRESHOLD,
  type KeyboardBinding,
} from './bindings'

export interface ButtonState {
  /** Held right now. */
  down: boolean
  /** Went down since a fixed step last consumed the frame (`consumePresses`). */
  pressed: boolean
  /** Went up since a fixed step last consumed the frame. */
  released: boolean
}

export interface PlayerInput {
  moveX: number
  moveZ: number
  /** Magnitude of the movement vector, 0..1 (analogue on a stick). */
  moveMag: number
  push: ButtonState
  dash: ButtonState
  wave: ButtonState
  brace: ButtonState
  vortex: ButtonState
  /** Which device is currently driving this player. */
  device: 'keyboard' | 'gamepad'
}

export interface InputFrame {
  /** One entry per fighter slot; bot slots are overwritten by the AI. */
  players: PlayerInput[]
  pausePressed: boolean
  gamepadConnected: boolean
}

export const InputResource = defineResource<InputFrame>('Input')

const ACTIONS = ['push', 'dash', 'wave', 'brace', 'vortex'] as const
type Action = (typeof ACTIONS)[number]

function newButton(): ButtonState {
  return { down: false, pressed: false, released: false }
}

function newPlayerInput(device: 'keyboard' | 'gamepad'): PlayerInput {
  return {
    moveX: 0,
    moveZ: 0,
    moveMag: 0,
    push: newButton(),
    dash: newButton(),
    wave: newButton(),
    brace: newButton(),
    vortex: newButton(),
    device,
  }
}

/** An idle frame with one slot per possible fighter. */
export function createInputFrame(): InputFrame {
  return {
    players: Array.from({ length: MAX_FIGHTERS }, () => newPlayerInput('keyboard')),
    pausePressed: false,
    gamepadConnected: false,
  }
}

/** Leaves a slot's input as a player who touches nothing. */
export function resetInput(input: PlayerInput): void {
  input.moveX = 0
  input.moveZ = 0
  input.moveMag = 0
  for (const action of ACTIONS) {
    input[action].down = false
    input[action].pressed = false
    input[action].released = false
  }
}

/**
 * Marks every press and release in the frame as handled.
 *
 * The devices are polled once per rendered frame, and a frame runs anywhere
 * from zero to five fixed steps. Presses therefore accumulate in the frame and
 * each fixed step consumes them, so a press reaches exactly one step: not lost
 * on a frame that runs none, not applied twice on a frame that runs two.
 */
export function consumePresses(frame: InputFrame): void {
  for (const player of frame.players) clearEdges(player)
}

function clearEdges(input: PlayerInput): void {
  for (const action of ACTIONS) {
    input[action].pressed = false
    input[action].released = false
  }
}

/**
 * Polls the keyboard and the Gamepad API and produces one normalised input
 * frame per player. Gameplay code only ever sees `PlayerInput`, so it cannot
 * tell — and must not care — which device a player is using.
 *
 * A gamepad takes over player 2 the moment it is plugged in, and hands control
 * back to the keyboard fallback if it disappears.
 */
export class InputManager {
  readonly frame: InputFrame = createInputFrame()

  private readonly keys = new Set<string>()
  private padIndex: number | null = null
  private prevPause = false
  /**
   * Escape went down since the last poll. A tap shorter than a frame is down
   * and up again between two polls, and the edge test alone never sees it —
   * the menu Esc opens is then a key that sometimes does nothing.
   */
  private pauseTapped = false
  private disposers: Array<() => void> = []
  /** Which fighter slots a human controls, in seating order. */
  private humanSlots: number[] = [0, 1]
  private readonly padScratch = newPlayerInput('gamepad')

  /**
   * Declares which slots are human.
   *
   * With a single human the keyboard and the gamepad are merged onto that one
   * slot, so playing against bots does not silently force you onto the
   * keyboard just because you happen to be sitting in seat one.
   */
  setHumanSlots(slots: number[]): void {
    this.humanSlots = slots.slice(0, MAX_HUMANS)
  }

  attach(target: Window = window): void {
    const onKeyDown = (e: KeyboardEvent) => {
      // Arrow keys and space scroll the page otherwise, which yanks the canvas
      // around — but a text field (a nick, a room code) needs them.
      const typing = (e.target as { tagName?: string } | null)?.tagName === 'INPUT'
      if (SCROLL_KEYS.has(e.code) && !typing) e.preventDefault()
      if (e.repeat) return
      this.keys.add(e.code)
      if (e.code === 'Escape') this.pauseTapped = true
    }
    const onKeyUp = (e: KeyboardEvent): void => {
      this.keys.delete(e.code)
    }
    const onBlur = () => this.keys.clear()
    const onPadConnected = (e: GamepadEvent) => {
      this.padIndex = e.gamepad.index
    }
    const onPadDisconnected = (e: GamepadEvent) => {
      if (this.padIndex === e.gamepad.index) this.padIndex = null
    }

    target.addEventListener('keydown', onKeyDown)
    target.addEventListener('keyup', onKeyUp)
    target.addEventListener('blur', onBlur)
    target.addEventListener('gamepadconnected', onPadConnected)
    target.addEventListener('gamepaddisconnected', onPadDisconnected)

    this.disposers = [
      () => target.removeEventListener('keydown', onKeyDown),
      () => target.removeEventListener('keyup', onKeyUp),
      () => target.removeEventListener('blur', onBlur),
      () => target.removeEventListener('gamepadconnected', onPadConnected),
      () => target.removeEventListener('gamepaddisconnected', onPadDisconnected),
    ]
  }

  dispose(): void {
    for (const d of this.disposers) d()
    this.disposers = []
  }

  /** True while any key is held — used to unlock the audio context. */
  get anyKeyDown(): boolean {
    return this.keys.size > 0
  }

  poll(): InputFrame {
    const pad = this.readPad()
    this.frame.gamepadConnected = pad !== null

    if (this.humanSlots.length <= 1) {
      const slot = this.humanSlots[0] ?? 0
      const target = this.frame.players[slot]
      this.pollKeyboard(target, P1_KEYS)
      if (pad) {
        // Merge rather than choose: whichever device the player actually
        // touches drives the fighter, and swapping mid-match just works. The
        // scratch carries this poll's edges only; the slot accumulates them.
        clearEdges(this.padScratch)
        this.pollGamepad(this.padScratch, pad)
        mergeInput(target, this.padScratch)
      }
    } else {
      this.pollKeyboard(this.frame.players[this.humanSlots[0]], P1_KEYS)
      const second = this.frame.players[this.humanSlots[1]]
      if (pad) this.pollGamepad(second, pad)
      else this.pollKeyboard(second, P2_KEYS)
    }

    const padPause = pad ? isPadButtonDown(pad, PAD_BINDING.pause) : false
    const pauseDown = this.keys.has('Escape') || padPause
    this.frame.pausePressed = (pauseDown && !this.prevPause) || this.pauseTapped
    this.pauseTapped = false
    this.prevPause = pauseDown

    return this.frame
  }

  private readPad(): Gamepad | null {
    const pads = navigator.getGamepads?.() ?? []
    if (this.padIndex !== null) {
      const p = pads[this.padIndex]
      if (p && p.connected) return p
      this.padIndex = null
    }
    for (const p of pads) {
      if (p && p.connected) {
        this.padIndex = p.index
        return p
      }
    }
    return null
  }

  private pollKeyboard(out: PlayerInput, binding: KeyboardBinding): void {
    const anyDown = (codes: string[]) => codes.some((c) => this.keys.has(c))

    let x = (anyDown(binding.right) ? 1 : 0) - (anyDown(binding.left) ? 1 : 0)
    // Screen "up" is −Z with the camera looking down +Z.
    let z = (anyDown(binding.down) ? 1 : 0) - (anyDown(binding.up) ? 1 : 0)

    const mag = Math.hypot(x, z)
    if (mag > 1) {
      x /= mag
      z /= mag
    }
    out.moveX = x
    out.moveZ = z
    out.moveMag = Math.min(mag, 1)
    out.device = 'keyboard'

    for (const action of ACTIONS) {
      updateButton(out[action], anyDown(binding[action]))
    }
  }

  private pollGamepad(out: PlayerInput, pad: Gamepad): void {
    let x = pad.axes[0] ?? 0
    let z = pad.axes[1] ?? 0

    // Radial deadzone with rescaling: a circular deadzone keeps diagonals
    // reachable, and rescaling avoids the velocity jump at the edge of it.
    const mag = Math.hypot(x, z)
    if (mag < STICK_DEADZONE) {
      x = 0
      z = 0
      out.moveMag = 0
    } else {
      const scaled = Math.min((mag - STICK_DEADZONE) / (1 - STICK_DEADZONE), 1)
      x = (x / mag) * scaled
      z = (z / mag) * scaled
      out.moveMag = scaled
    }
    out.moveX = x
    out.moveZ = z
    out.device = 'gamepad'

    const dpadX =
      (isPadButtonDown(pad, 15) ? 1 : 0) - (isPadButtonDown(pad, 14) ? 1 : 0)
    const dpadZ = (isPadButtonDown(pad, 13) ? 1 : 0) - (isPadButtonDown(pad, 12) ? 1 : 0)
    if (dpadX !== 0 || dpadZ !== 0) {
      const dm = Math.hypot(dpadX, dpadZ)
      out.moveX = dpadX / dm
      out.moveZ = dpadZ / dm
      out.moveMag = 1
    }

    updateButton(out.push, isPadButtonDown(pad, PAD_BINDING.push))
    updateButton(out.dash, isPadButtonDown(pad, PAD_BINDING.dash))
    updateButton(out.wave, isPadButtonDown(pad, PAD_BINDING.wave))
    updateButton(out.vortex, isPadButtonDown(pad, PAD_BINDING.vortex))
    updateButton(
      out.brace,
      (pad.buttons[PAD_BINDING.brace]?.value ?? 0) > TRIGGER_THRESHOLD ||
        isPadButtonDown(pad, PAD_BINDING.brace),
    )
  }

  /** Short rumble on the active gamepad; silently ignored where unsupported. */
  rumble(strength: number, durationMs: number): void {
    if (this.padIndex === null) return
    const pad = (navigator.getGamepads?.() ?? [])[this.padIndex]
    const actuator = pad?.vibrationActuator
    if (!actuator || typeof actuator.playEffect !== 'function') return

    const s = Math.max(0, Math.min(1, strength))
    void actuator
      .playEffect('dual-rumble', {
        startDelay: 0,
        duration: durationMs,
        weakMagnitude: s,
        strongMagnitude: s * 0.8,
      })
      .catch(() => undefined)
  }
}

/** Folds a second device's state into a slot: buttons OR, stick takes over. */
function mergeInput(target: PlayerInput, other: PlayerInput): void {
  if (other.moveMag > target.moveMag) {
    target.moveX = other.moveX
    target.moveZ = other.moveZ
    target.moveMag = other.moveMag
    target.device = 'gamepad'
  }
  for (const action of ACTIONS) {
    const a = target[action]
    const b = other[action]
    a.pressed = a.pressed || b.pressed
    a.released = a.released || b.released
    a.down = a.down || b.down
  }
}

function isPadButtonDown(pad: Gamepad, index: number): boolean {
  const b = pad.buttons[index]
  return b ? b.pressed || b.value > 0.5 : false
}

/** Edges accumulate until a fixed step consumes them; see `consumePresses`. */
function updateButton(state: ButtonState, down: boolean): void {
  state.pressed = state.pressed || (down && !state.down)
  state.released = state.released || (!down && state.down)
  state.down = down
}

const SCROLL_KEYS = new Set([
  'Space',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
])

export type { Action }
export { ACTIONS }
